import DailyNoteAttachmentGallery from "./DailyNoteAttachmentGallery";

const stripHtmlText = (value = "") => String(value || "")
  .replace(/<[^>]+>/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const formatDate = (value) => {
  if (!value) return "Not set";
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? "Not set"
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not set"
    : date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
};

const noteKey = (note = {}, index = 0) =>
  note.id || `${note.title || "daily-note"}-${note.noteDate || note.date || index}`;

const reporterName = (note = {}) =>
  note.reportedByName || note.createdByName || note.authorName || note.reporterName || "Care staff";

const updaterName = (note = {}) =>
  note.updatedByName || note.modifiedByName || reporterName(note);

const wasUpdated = (note = {}) => {
  if (!note.updatedAt) return false;
  if (!note.createdAt) return true;
  const createdAt = new Date(note.createdAt).getTime();
  const updatedAt = new Date(note.updatedAt).getTime();
  return Number.isNaN(createdAt) || Number.isNaN(updatedAt) ? note.updatedAt !== note.createdAt : updatedAt > createdAt;
};

const timeRange = (note = {}) => {
  const start = note.startTimestamp || note.rangeStartTimestamp || note.startedAt || note.date || note.happenedAt || note.occurredAt;
  const end = note.endTimestamp || note.rangeEndTimestamp || note.endedAt;
  if (!end || end === start) return formatDateTime(start);
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};

const quickSelectionLabel = (selection) => {
  if (typeof selection === "string") return selection;
  return selection?.label || selection?.name || selection?.title || selection?.value || "";
};

const noteDateParts = (value) => {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ? `${value}T12:00:00` : value;
  const date = new Date(normalized || "");
  if (Number.isNaN(date.getTime())) return { month: "---", day: "--", year: "" };
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }),
    day: date.toLocaleDateString("en-US", { day: "2-digit" }),
    year: date.toLocaleDateString("en-US", { year: "numeric" }),
  };
};

export default function DailyNoteList({ notes = [], onSelect, onView, onEdit, onDelete, emptyMessage = "No daily notes have been added to this care process.", variant = "compact" }) {
  if (!notes.length) return <p className="probation-empty">{emptyMessage}</p>;

  return (
    <div className="probation-note-list daily-notes-shared-list">
      {notes.map((note, index) => {
        const title = note.title || note.category || "Daily note";
        const summary = stripHtmlText(note.summary || note.notes || note.description) || "No written summary added.";
        const actionTaken = stripHtmlText(note.interventionAction || note.intervention || note.actionTaken);
        const attachments = Array.isArray(note.photos) ? note.photos : [];
        const selections = Array.isArray(note.quickSelections) ? note.quickSelections.map(quickSelectionLabel).filter(Boolean) : [];
        const dateParts = noteDateParts(note.noteDate || note.date);
        const content = (
          <>
            {variant === "page" ? (
              <div className="daily-note-date-block" aria-label={formatDate(note.noteDate || note.date)}>
                <span>{dateParts.month}</span><strong>{dateParts.day}</strong><small>{dateParts.year}</small>
              </div>
            ) : null}
            <div className="daily-note-readable-content">
            <div className="probation-note-card-head">
              <div>
                <strong>{title}</strong>
                {variant !== "page" ? <span>Note date: {formatDate(note.noteDate || note.date)}</span> : null}
              </div>
              <div className="daily-note-head-tools">
                {note.category ? <em>{note.category}</em> : null}
                {variant === "page" && (onView || onEdit || onDelete) ? (
                  <div className="daily-note-row-actions">
                    {onView ? <button type="button" title="View details" aria-label={`View ${title}`} onClick={() => onView(note)}><i className="ti ti-eye" /></button> : null}
                    {onEdit ? <button className="daily-note-update" type="button" title="Update note" aria-label={`Update ${title}`} onClick={() => onEdit(note)}><i className="ti ti-edit" /></button> : null}
                    {onDelete ? <button className="delete" type="button" title="Delete note" aria-label={`Delete ${title}`} onClick={() => onDelete(note)}><i className="ti ti-trash" /></button> : null}
                  </div>
                ) : null}
              </div>
            </div>
            {selections.length ? (
              <div className="quick-note-selected-list">
                {selections.map((selection) => <span key={selection}>{selection}</span>)}
              </div>
            ) : null}
            <div className="daily-note-description"><span>Description</span><p className="daily-note-narrative">{summary}</p></div>
            {actionTaken ? <div className="daily-note-action-taken"><span>Action taken</span><p>{actionTaken}</p></div> : null}
            {variant === "page" && attachments.length ? (
              <div className="daily-note-attachments">
                <span>Attachments</span>
                <DailyNoteAttachmentGallery attachments={attachments} compact />
              </div>
            ) : null}
            <div className="probation-note-card-meta">
              <span><i className="ti ti-user" /> {reporterName(note)}</span>
              <span><i className="ti ti-clock" /> {timeRange(note)}</span>
              {wasUpdated(note) ? <span><i className="ti ti-edit" /> Updated by {updaterName(note)}</span> : null}
              {wasUpdated(note) ? <span><i className="ti ti-history" /> Updated {formatDateTime(note.updatedAt)}</span> : null}
              {attachments.length ? <span><i className="ti ti-paperclip" /> {attachments.length} attachment{attachments.length === 1 ? "" : "s"}</span> : null}
            </div>
            </div>
          </>
        );

        const cardClassName = `probation-note-card${variant === "page" ? " daily-note-readable-card" : ""}`;

        return onSelect ? (
          <button className={cardClassName} data-readonly-allowed="true" type="button" onClick={() => onSelect(note)} key={noteKey(note, index)}>
            {content}
          </button>
        ) : (
          <article className={cardClassName} key={noteKey(note, index)}>{content}</article>
        );
      })}
    </div>
  );
}
